---
title: "Latah Aux Comm Bylaws"
adopted: 2024-01-01
updated: 2024-01-01
---

# Latah Aux Comm Bylaws

## Article I: Purpose and Mission

### Section 1: Purpose

The purpose of the Latah Aux Comm is to provide training, public outreach, and operational support for the Latah County Auxiliary Communications (Aux Comm) Team. The program serves as the training section for the county team and acts as the public-facing entity for community events, enabling greater access to donations and financial support for training and operations.

### Section 2: Mission

The mission of the Latah Aux Comm is to:

1. Enhance the operational capabilities of the Latah County Aux Comm Team through comprehensive training programs.
2. Facilitate public service by supporting community events and disaster preparedness initiatives.
3. Promote collaboration and resource sharing between the Latah Aux Comm and the Do Something Amateur Radio Club (DSARC).
4. Foster public awareness of auxiliary communications and their role in emergency response.
5. Prioritize team and scene safety during all program activities, ensuring that safety remains foundational to the program’s operations.

---

## Article II: Membership

### Section 1: Eligibility

Membership in the Latah Aux Comm is open to:

1. Members of the Latah County Aux Comm Team.
2. Individuals who commit to becoming members of the Latah County Aux Comm Team within six months of joining the program.

### Section 2: Membership Requirements

1. All members must be either voting or non-voting members of the DSARC.
2. Membership attendance requirements:
   - Attend at least two meetings, events, or training sessions per quarter.
   - Attend at least two spontaneous deployments per year, with six opportunities provided when possible.
   - Participate in at least **four Latah Aux Comm/ARES nets per quarter**. Exceptions must be requested and approved in advance. Participation in other nets may be considered if submitted in writing.
   - Exceptions to attendance requirements must be requested in advance and approved in writing by the officers.
3. Membership is subject to approval by the program officers and may be revoked at any time without cause. Revocation of program membership does not affect DSARC club membership.

### Section 3: Membership Types

1. **Active Members**: Members who meet the attendance and participation requirements and actively engage in program activities.
2. **Provisional Members**: New members who must fulfill the requirement of joining the Latah County Aux Comm Team within six months of joining the program.

---

## Article III: Governance

### Section 1: Authority

The Latah Aux Comm operates as a program under the governance of the Do Something Amateur Radio Club, Inc. (DSARC, Inc.) board of directors. The program officers report to the board and operate in alignment with DSARC, Inc.’s mission and policies.

### Section 2: Officers

1. Officers of the program are appointed by the DSARC, Inc. board of directors and are not elected.
2. Officers will include:
   - **Program Manager (Unit Commander)**: Oversees the program’s operations, training, and public-facing activities.
   - **Deputy Program Manager (Deputy Unit Commander)**: Assists the Program Manager and serves as second-in-command.
   - **Red Team Leader**: Coordinates team operations for deployment and field activities, typically for the first operational period.
   - **Blue Team Leader**: Coordinates team operations for deployment and field activities, typically for the second operational period.
   - **Safety Officer**: Ensures safety protocols are followed during all activities and events. In the absence of the Safety Officer, a qualified safety lead shall be appointed from responding members.
   - **Training Coordinator**: Develops and implements training programs for members.
   - **Public Events Coordinator**: Manages public service and community event participation.
   - **Logistics Officer**: Ensures operational readiness and manages program resources.

3. Members may hold multiple officer positions if needed.

### Section 3: Teams and Positions

1. The program’s structure will mirror that of the Latah County Aux Comm Team to ensure continuity and operational alignment.
2. Team members may hold specialized roles (e.g., Net Control, Technical Specialist) based on their skills and training.

---

## Article IV: Meetings and Participation

### Section 1: Regular Meetings

The Latah Aux Comm shall hold regular monthly meetings for training, planning, and coordination. These meetings will often be held in conjunction with DSARC meetings, but alternate or additional meetings may occur with no less than 2 days’ notice.

### Section 2: Participation

1. Members must actively participate in program activities, including trainings, events, and spontaneous deployments.
2. Members must maintain compliance with attendance requirements as outlined in Article II, Section 2.

### Section 3: Special Meetings

Special meetings may be called by the Program Manager or the DSARC, Inc. board of directors to address urgent matters or special initiatives. Members shall be notified at least 2 days in advance.

---

## Article V: Financial Management

### Section 1: General Fund

1. All funds raised or donated to the program shall be deposited into the **general fund** managed by DSARC, Inc.
2. The DSARC, Inc. board shall allocate budgets for program operations and training based on approved plans.

### Section 2: Donations

1. Donations received during public events or other activities shall be earmarked for training and program-specific purposes unless otherwise noted by the donor.
2. Financial reports shall be provided to both the program membership and the DSARC, Inc. board.

---

## Article VI: Feedback and Safety Mechanisms

### Section 1: Leadership Feedback

1. Members may provide feedback or raise complaints regarding program leadership to the DSARC, Inc. board.
2. Complaints or concerns must be submitted in writing and will be reviewed during the next scheduled board meeting.
3. Decisions regarding leadership changes or adjustments shall be at the discretion of the DSARC, Inc. board.

### Section 2: Safety Concerns

1. Safety concerns or violations during activities must be reported immediately to the Safety Officer or, in their absence, the acting safety lead.
2. Safety concerns that cannot be resolved on-site must be documented and reported to the DSARC, Inc. board for review.

### Section 3: Team and Scene Safety

1. Safety is foundational to all program activities, and all members are expected to prioritize safety protocols.
2. If the designated Safety Officer is unable to attend an event, a qualified safety lead shall be appointed from responding members, preferably someone with prior safety experience.

---

## Article VII: Membership Approval and Revocation

### Section 1: Approval

1. Membership applications must be reviewed and approved by the program officers.
2. New members must become members of the Latah County Aux Comm Team within six months of joining the program.

### Section 2: Revocation

Membership may be revoked at any time by the program officers without cause. Revocation shall not affect the individual’s DSARC club membership status.

---

## Article VIII: Amendments

These bylaws may be amended by the DSARC, Inc. board of directors. Proposed amendments must be submitted to the Program Manager for review and discussion with the program membership prior to board approval.

---

## Article IX: Compliance

### Section 1: Oversight

The Latah Aux Comm shall operate in compliance with the bylaws, policies, and financial procedures of DSARC, Inc.

### Section 2: Alignment

All activities, decisions, and training initiatives shall align with the mission and goals of both the Latah County Aux Comm Team and DSARC, Inc.

---
